import React, { useEffect, useState } from "react";
import SelectNewPlanStep from "./SelectNewPlanStep";
import {
  CurrentUserSubscriptionFragment,
  useSwitchUserSubscriptionMutation,
} from "../SwitchPlanDrawer-generated.hooks";
import { useForm, useWatch } from "react-hook-form";
import { SwitchPlanForm } from "./SwitchPlanForm.utils";
import SelectDiscountStep from "./SelectDiscountStep";
import SelectWhenToSwitchStep from "./SelectWhenToSwitchStep";
import SelectPaymentMethodStep from "./SelectPaymentMethodStep";
import { useToggle } from "@pushpress/shared-components";
import { toast } from "react-toastify";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { getBillingUuid } from "../../../components/controllers/UserBillingSelect/UserBillingSelect.utils";
import SetSetupFeeStep from "./SetSetupFeeStep";

type SwitchPlanDrawerContentProps = {
  userSubscription: CurrentUserSubscriptionFragment;
  userUuid: string;
};

type Steps =
  | "chooseNewPlan"
  | "chooseDiscount"
  | "chooseSetupFee"
  | "chooseWhenToSwitch"
  | "choosePaymentMethod";

function SwitchPlanForm({
  userSubscription,
  userUuid,
}: SwitchPlanDrawerContentProps) {
  const { control, trigger, handleSubmit, setValue } = useForm<SwitchPlanForm>({
    mode: "onTouched",
    defaultValues: {
      discount: userSubscription.discount,
    },
  });
  const [newPlan] = useWatch({
    control,
    name: ["newPlan"],
  });
  const [switchSubscription] = useSwitchUserSubscriptionMutation();
  const [step, setStep] = useState<Steps>("chooseNewPlan");
  const [loadingSwitchingSubscription, loadingSwitchingSubscriptionActions] =
    useToggle(false);

  const submit = async (values: SwitchPlanForm) => {
    try {
      loadingSwitchingSubscriptionActions.on();
      const response = await switchSubscription({
        variables: {
          switchUserSubscriptionInput: {
            billingUuid: getBillingUuid(values.paymentMethod.uuid),
            newPlanUuid: values.newPlan.planUuid,
            userSubscriptionUuid: userSubscription.uuid,
            userSubscriptionSwitchCriteria: values.whenToSwitch,
            discountUuid: values.discount?.discountUuid,
            setupFee: values.setupFee,
          },
        },
      });

      switch (response.data?.switchUserSubscription.__typename) {
        case "SwitchUserSubscriptionSuccessResult":
          toast.success(
            "Subscription switched successfully.",
            defaultToastOptions,
          );
          if (!window.Cypress) {
            window.location.reload();
          }
          // We do not want to turn off loadingSwitchingSubscription here
          // this happens because we are on a PHP page and we want to refresh the whole page
          break;
        case "SwitchUserSubscriptionFailedResult":
          toast.error(
            `Error switching subscription: ${response.data?.switchUserSubscription.message}`,
            defaultToastOptions,
          );
          loadingSwitchingSubscriptionActions.off();
          break;
        case "SwitchUserSubscriptionBillingNeedActionResult":
          toast.success(
            `Subscription switched successfully. An email has been sent for them to finalize the payment process.`,
            defaultToastOptions,
          );
          if (!window.Cypress) {
            window.location.reload();
          }
          // We do not want to turn off loadingSwitchingSubscription here
          // this happens because we are on a PHP page and we want to refresh the whole page
          break;
        case "SwitchUserSubscriptionBillingFailedResult":
          toast.error(
            `Error billing client: ${response.data?.switchUserSubscription.message}`,
            defaultToastOptions,
          );
          loadingSwitchingSubscriptionActions.off();
          break;
      }
    } catch (err) {
      toast.error(
        `An error ocurred during switching subscription: ${extractError(err)}`,
        defaultToastOptions,
      );
      loadingSwitchingSubscriptionActions.off();
    }
  };

  // when newPlan changes, set the setup fee
  useEffect(() => {
    if (newPlan) {
      setValue("setupFee", Number(newPlan.setupFee));
    }
  }, [newPlan, setValue]);

  return (
    <div className="p-3">
      {step === "chooseNewPlan" && (
        <SelectNewPlanStep
          control={control}
          userSubscription={userSubscription}
          goToNextStep={(newPlan?: SwitchPlanForm["newPlan"]) => {
            if (newPlan) {
              setStep("chooseDiscount");
              return;
            }

            trigger("newPlan");
          }}
        />
      )}
      {step === "chooseDiscount" && (
        <SelectDiscountStep
          control={control}
          userSubscription={userSubscription}
          goToNextStep={() => {
            setStep("chooseSetupFee");
          }}
          goBack={() => setStep("chooseNewPlan")}
        />
      )}
      {step === "chooseSetupFee" && (
        <SetSetupFeeStep
          control={control}
          userSubscription={userSubscription}
          goToNextStep={() => {
            setStep("chooseWhenToSwitch");
          }}
          goBack={() => setStep("chooseDiscount")}
        />
      )}
      {step === "chooseWhenToSwitch" && (
        <SelectWhenToSwitchStep
          control={control}
          userSubscription={userSubscription}
          goBack={() => setStep("chooseSetupFee")}
          goToNextStep={(whenToSwitch?: SwitchPlanForm["whenToSwitch"]) => {
            if (whenToSwitch) {
              setStep("choosePaymentMethod");
              return;
            }

            trigger("whenToSwitch");
          }}
        />
      )}
      {step === "choosePaymentMethod" && (
        <SelectPaymentMethodStep
          control={control}
          userSubscription={userSubscription}
          userUuid={userUuid}
          goBack={() => setStep("chooseWhenToSwitch")}
          goToNextStep={handleSubmit(submit)}
          loading={loadingSwitchingSubscription}
        />
      )}
    </div>
  );
}

export default SwitchPlanForm;
